<script lang="ts">
  import { Header, type HeaderProps, type MenuItem } from '@stickerapp-org/athena/Header';
  import { Type } from '@sinclair/typebox';
  import type { SearchResult } from '@athena-lib/Header/SearchBar/types';
  import { getLoginContext } from '@athena-lib/loginContext.svelte';
  import type { ShippingDetails } from '@athena-lib/Header/Header/types';
  import { goto } from '$app/navigation';
  import { page } from '$app/state';
  import { getNameByLinkType, getUrlByLinkType } from '$lib/utilities/storyblok';
  import { post } from '$lib/http';
  import { getShoppingCart } from '$lib/shopping-cart.svelte';
  import type { ExpandedHeader } from '$lib/server/expanders/header';
  import { browser } from '$app/environment';
  import { GtmEventNames, type SearchEvent } from '$lib/gtm/types';
  import { trackSearch } from '$lib/gtm/event-trigger';
  import { getCheckoutSdk } from '$lib/checkout.svelte';
  import { checkoutDrawerActions } from '$lib/utilities/checkoutDrawerContext.svelte';
  import { getCartEventsTracker } from '$lib/gtmEventHelper.svelte';

  const {
    blok,
    transparentAtTop,
    useNewCheckout,
    shippingDetails,
  }: {
    blok: ExpandedHeader;
    transparentAtTop?: boolean;
    useNewCheckout?: boolean;
    shippingDetails?: ShippingDetails;
  } = $props();

  const shoppingCart = getShoppingCart();
  const cartItems = $derived(shoppingCart.count);
  const checkoutSdk = $derived(getCheckoutSdk());

  const cartEventsTracker = getCartEventsTracker('cart_icon');

  const searchQuery = $derived.by(() => {
    if (!browser) {
      return undefined;
    }
    const result = page.url.searchParams.get('q') ?? undefined;
    return result;
  });

  function setSearchQuery(query: string | undefined) {
    const searchParams = new URLSearchParams(page.url.searchParams.toString());

    if (query !== undefined) {
      const event: SearchEvent = {
        event: GtmEventNames.SearchResultViewed,
        event_params: {
          event_action: 'search',
          query,
        },
      };
      if (query !== '') {
        trackSearch(event);
      }
      searchParams.set('q', query);
    }
    if (query === undefined) {
      searchParams.delete('q');
    }

    goto(`?${searchParams.toString()}`);
  }

  function trackGtmSearchClick(item: SearchResult, index: number, query?: string) {
    const event: SearchEvent = {
      event: GtmEventNames.SearchResultClicked,
      event_params: {
        event_action: 'search',
        label: 'link_click',
        query_id: item.query_id,
        query: query ?? '',
        item_url: item.url?.replace(/\?.*$/, '') ?? '',
        index,
      },
    };
    trackSearch(event);
  }
  let drawerIsOpen = $state(false);
  const isLoggedIn = $derived(getLoginContext().isLoggedIn);
  const closeDrawer = () => {
    drawerIsOpen = false;
  };
  const headerProps: HeaderProps = $derived.by(() => {
    const desktopMenuItems: MenuItem[] = blok.desktopItems.map((item) => ({
      text: getNameByLinkType(item),
      link: getUrlByLinkType(item.link),
    }));

    const desktopDrawerMainMenuItems: MenuItem[] = (blok.desktopDrawerMainItems ?? []).map(
      (item) => ({
        text: getNameByLinkType(item),
        link: getUrlByLinkType(item.link),
      }),
    );

    const desktopDrawerSecondaryMenuItems: MenuItem[] = (
      blok.desktopDrawerSecondaryItems ?? []
    ).map((item) => ({
      text: getNameByLinkType(item),
      link: getUrlByLinkType(item.link),
    }));

    const desktopDrawerTertiaryMenuItems: MenuItem[] = (blok.desktopDrawerTertiaryItems ?? []).map(
      (item) => ({
        text: getNameByLinkType(item),
        link: getUrlByLinkType(item.link),
      }),
    );

    const mobileMainMenuItems: MenuItem[] = (blok.mobileMainItems ?? []).map((item) => ({
      text: getNameByLinkType(item),
      link: getUrlByLinkType(item.link),
    }));

    const mobileSecondaryMenuItems: MenuItem[] = (blok.mobileSecondaryItems ?? []).map((item) => ({
      text: getNameByLinkType(item),
      link: getUrlByLinkType(item.link),
    }));

    const mobileTertiaryMenuItems: MenuItem[] = (blok.mobileTertiaryItems ?? []).map((item) => ({
      text: getNameByLinkType(item),
      link: getUrlByLinkType(item.link),
    }));

    const infoBanners: HeaderProps['infoBanners'] =
      blok.infoBanners?.map((banner) => {
        return {
          id: banner._uid,
          content: banner.info ?? '',
        };
      }) ?? [];

    return {
      ...blok,
      currentUrl: page.url,
      desktopMenuItems,
      desktopDrawerMainMenuItems,
      desktopDrawerSecondaryMenuItems,
      desktopDrawerTertiaryMenuItems,
      mobileMainMenuItems,
      mobileSecondaryMenuItems,
      mobileTertiaryMenuItems,
      signInLinkText: blok.signInLinkText ?? '',
      reorderLinkText: blok.reorderLinkText ?? '',
      accountLinkText: blok.accountLinkText ?? '',
      infoBanners,
      stickerAppLogoUrl: blok.logo.filename ?? '',
      dismissInfoBanner: async (infoBanner) => {
        await post(
          '/api/info-banner',
          {
            action: 'dismiss',
            id: infoBanner.id,
          },
          Type.Unknown(),
        );
      },
      openDrawer: () => {
        drawerIsOpen = true;
      },
      closeDrawer,
      drawerIsOpen,
      searchQuery,
      setSearchQuery,
      openSearch: () => {
        if (searchQuery === undefined) {
          setSearchQuery('');
        }
        const event: SearchEvent = {
          event: GtmEventNames.SearchBarOpened,
          event_params: {
            event_action: 'search',
          },
        };
        trackSearch(event);
      },
      closeSearch: () => {
        setSearchQuery(undefined);
      },
      onResultClicked(item, index, query) {
        trackGtmSearchClick(item, index, query);
      },
      itemsInCart: cartItems,
      transparentAtTop: transparentAtTop ?? false,
      onCheckoutClick: () => {
        cartEventsTracker.viewReviewCart();
        if (checkoutSdk) {
          window.location.href = `${checkoutSdk.state.checkoutPageUrl}?preview`;
        } else {
          window.location.href = blok.checkoutUrl;
        }
      },
      onAccountClick: (e) => {
        closeDrawer();
        if (isLoggedIn) {
          goto('/customer');
        } else {
          if (checkoutSdk) {
            checkoutDrawerActions.openLogin(e.currentTarget);
          } else {
            goto('#modal-login');
          }
        }
      },
      shippingDetails,
      checkoutSdk,
    };
  });
</script>

<Header
  {...headerProps}
  navigate={(url) => {
    goto(url);
  }}
  {useNewCheckout}
/>
